import React from "react"
import { observer } from "mobx-react-lite"
import styled from "~/ui/styled"
import {
  BetweenFlex,
  CenterFlex,
  ColumnFlex,
  IconFont,
  StartFlex,
  Text,
} from "~components/El"
import { useTheme } from "~/ui/styled"
import { ActivityIndicator, SectionList, SectionListData } from "~/ui/primitives"
import { useSearchParams } from "react-router-dom"
import { formatMessageTime } from "~utils"
import { useNoticeCenter, type NoticeItem, type NoticeSection } from "./notice-center-context"

const Container = styled(ColumnFlex)`
  flex: 1;
  min-height: 0;
  width: 100%;
  padding: ${({ theme }) => theme.space.sm};
  background: ${({ theme }) => theme.colors.blockBackground};
`

const NoticeRow = ({
  item,
  theme,
  icon,
  isFirst,
  isLast,
  onClick,
}: {
  item: NoticeItem
  theme: any
  icon: string
  isFirst: boolean
  isLast: boolean
  onClick: () => void
}) => {
  const highlighted = item.status === "unread"
  return (
    <CenterFlex
      width="auto"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      style={{
        height: 62,
        backgroundColor: highlighted
          ? theme.colors.primary + "1a"
          : theme.colors.blockBackground,
        paddingRight: 10,
        borderTopLeftRadius: isFirst ? 8 : 0,
        borderTopRightRadius: isFirst ? 8 : 0,
        borderBottomLeftRadius: isLast ? 8 : 0,
        borderBottomRightRadius: isLast ? 8 : 0,
        overflow: "hidden",
        cursor: "pointer",
      }}
      onClick={onClick}
    >
      <StartFlex
        style={{
          padding: 10,
          width: "auto",
          height: "auto",
          borderLeftWidth: 4,
          borderLeftColor: highlighted ? theme.colors.primary : "transparent",
          borderLeftStyle: "solid",
        }}
      >
        <CenterFlex
          height="auto"
          style={{
            width: "auto",
            borderRadius: 9999,
            padding: 6,
            backgroundColor: highlighted
              ? theme.colors.primary + "2a"
              : theme.colors.background,
          }}
        >
          <IconFont
            name={icon}
            size={18}
            color={highlighted ? theme.colors.primary : theme.colors.secondary}
          />
        </CenterFlex>
      </StartFlex>
      <BetweenFlex
        height="100%"
        width="auto"
        style={{
          borderBottomWidth: 1,
          borderBottomColor: isLast ? "transparent" : theme.colors.border,
          borderBottomStyle: "solid",
          flex: 1,
          height: "100%",
        }}
      >
        <ColumnFlex height="auto" justifyContent="center" paddingRight="10px">
          <BetweenFlex height="auto">
            <Text
              style={{
                fontWeight: "600",
                width: "auto",
                flex: 1,
                color: highlighted ? theme.colors.text : theme.colors.infoText,
                fontSize: 14,
                lineHeight: 24,
              }}
            >
              {item.label}
            </Text>
            <Text color={theme.colors.infoText}>{formatMessageTime(item.created)}</Text>
          </BetweenFlex>
          {!!item.content && (
            <Text
              style={{
                width: "auto",
                fontSize: 12,
                lineHeight: 24,
                color: highlighted ? theme.colors.text : theme.colors.secondaryText,
              }}
            >
              {item.content}
            </Text>
          )}
        </ColumnFlex>
        <CenterFlex height="auto" width="40px">
          <IconFont name="right" size={14} color={theme.colors.secondaryText} />
        </CenterFlex>
      </BetweenFlex>
    </CenterFlex>
  )
}

const NoticeCenterList: React.FC = () => {
  const theme = useTheme()
  const [searchParams, setSearchParams] = useSearchParams()
  const { sections, refreshing, loadingMore, onRefresh, onEndReached } = useNoticeCenter()

  const selectNotice = (id: string | number) => {
    setSearchParams({ notice_id: String(id) }, { replace: true })
  }

  return (
    <Container>
      <SectionList
        style={{ flex: 1, minHeight: 0, height: "auto" }}
        sections={sections as unknown as SectionListData<NoticeItem, NoticeSection>[]}
        keyExtractor={(item) => String(item.id)}
        refreshing={refreshing}
        onRefresh={onRefresh}
        onEndReached={onEndReached}
        onEndReachedThreshold={0.3}
        stickySectionHeadersEnabled={false}
        renderSectionHeader={({ section }) => {
          const unreadCount = section.data.filter((x) => x.status === "unread").length
          return (
            <BetweenFlex height="auto" width="100%" padding="13px 0">
              <StartFlex width="auto" height="auto">
                <IconFont
                  marginRight="8"
                  name={section.icon}
                  size={16}
                  color={theme.colors.primary}
                />
                <Text fontSize="lg" fontWeight="600">
                  {section.label}
                </Text>
              </StartFlex>
              {unreadCount > 0 ? (
                <CenterFlex
                  width="auto"
                  height="auto"
                  padding="0 10px"
                  bg="#ff3b30"
                  borderRadius="10px"
                >
                  <Text color="white" lineHeight="16px">
                    {unreadCount}
                  </Text>
                </CenterFlex>
              ) : null}
            </BetweenFlex>
          )
        }}
        renderItem={({ item, section, index }) => (
          <NoticeRow
            item={item}
            theme={theme}
            icon={section.icon}
            isFirst={index === 0}
            isLast={section.data.length - 1 === index}
            onClick={() => selectNotice(item.id)}
          />
        )}
        ListFooterComponent={() =>
          loadingMore ? (
            <CenterFlex padding="12px 0">
              <ActivityIndicator color={theme.colors.primary} />
            </CenterFlex>
          ) : null
        }
      />
    </Container>
  )
}

export default observer(NoticeCenterList)
